import React, { useState, useEffect } from 'react';
import { cleanApiBaseUrl, publicApi, rag } from '../lib/api';
import { Search, FileText, Download, Globe, ChevronRight, Filter, Sparkles, Send } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const DOC_TYPES = ['Resolution','Ordinance','Meeting Minutes','Memorandum','Map','Contract','Report','Other'];

function TypeBadge({ type }) {
  const colors = {
    'Resolution': 'bg-blue-50 text-blue-700 border-blue-100',
    'Ordinance': 'bg-purple-50 text-purple-700 border-purple-100',
    'Meeting Minutes': 'bg-teal-50 text-teal-700 border-teal-100',
    'Map': 'bg-amber-50 text-amber-700 border-amber-100',
    'Report': 'bg-green-50 text-green-700 border-green-100',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${colors[type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{type}</span>;
}

export default function PublicPortal() {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ type: '', department: '' });
  const [results, setResults] = useState(null);
  const [facets, setFacets] = useState({ types: [], departments: [], total_public: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searched, setSearched] = useState(false);
  const [lang, setLang] = useState('en');
  const [cityName, setCityName] = useState('the City');
  const apiBase = cleanApiBaseUrl();

  const T = {
    en: {
      title: 'CogniVault', sub: 'Public Records Portal', heroTitle: 'Search Public Records',
      placeholder: 'Search resolutions, ordinances, minutes, maps...',
      searchBtn: 'Search', allDepts: 'All departments', noResults: 'No public records match your search',
      download: 'Download', staffLogin: 'Staff login', footer: cityName === 'Generic' ? 'CogniVault Public Records Portal' : `CogniVault ${cityName} Public Records Portal`,
      askTitle: `Have a question about ${cityName === 'Generic' ? 'official' : cityName + "'s"} records?`, askSub: 'Ask in plain English — our AI searches published records to find the answer',
      askPlaceholder: '"What are the latest council decisions?"', askBtn: 'Ask', aiDisclaimer: 'AI-generated answer — verify against source documents for official use',
      sources: 'Source records', clearSearch: 'Clear search',
    },
    es: {
      title: 'CogniVault', sub: 'Portal de Registros Públicos', heroTitle: 'Buscar Registros Públicos',
      placeholder: 'Buscar resoluciones, ordenanzas, actas, mapas...',
      searchBtn: 'Buscar', allDepts: 'Todos los departamentos', noResults: 'No se encontraron registros públicos',
      download: 'Descargar', staffLogin: 'Acceso personal', footer: `Portal de Registros Públicos de ${cityName === 'Generic' ? 'CogniVault' : cityName}`,
      askTitle: `¿Tiene preguntas sobre los registros de ${cityName === 'Generic' ? 'la Ciudad' : cityName}?`, askSub: 'Pregunte en español — nuestra IA busca en los registros publicados para encontrar la respuesta',
      askPlaceholder: '"¿Cuáles son las últimas decisiones del concejo?"', askBtn: 'Preguntar', aiDisclaimer: 'Respuesta generada por IA — verifique con los documentos originales para uso oficial',
      sources: 'Documentos de origen', clearSearch: 'Limpiar búsqueda',
    }
  }[lang];
  const [ragQuestion, setRagQuestion] = useState('');
  const [ragAnswer, setRagAnswer] = useState(null);
  const [ragLoading, setRagLoading] = useState(false);

  const askCitizen = async () => {
    if (!ragQuestion.trim()) return;
    setRagLoading(true);
    setRagAnswer(null);
    try {
      // No top_k — server-side default, same reason as AskPage (Finding #98).
      const { data } = await rag.askPublic(ragQuestion.trim());
      setRagAnswer(data);
    } catch (e) {
      setRagAnswer({ answer: 'Sorry, the Q&A service is currently unavailable. Please use the search above.', sources: [] });
    } finally {
      setRagLoading(false);
    }
  };

  useEffect(() => {
    publicApi.facets().then(r => setFacets(r.data)).catch(() => {});
    
    // Fetch city config for branding
    fetch(`${apiBase}/api/config`)
      .then(r => r.json())
      .then(data => { if (data.city_name) setCityName(data.city_name); })
      .catch(() => {});

    // Load recent on mount
    doSearch(1, true);
  }, []);

  const doSearch = async (newPage = 1, initial = false) => {
    setLoading(true);
    if (!initial) setSearched(true);
    setPage(newPage);
    try {
      const { data } = await publicApi.search({ q: initial ? '' : q, ...filters, page: newPage, limit: 12 });
      setResults(data);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter') doSearch(1); };



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cognivault_logo.png" alt="CogniVault" className="h-10 w-auto object-contain" />
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              <button onClick={() => setLang('en')} aria-label="Switch to English"
                className={`px-3 py-1.5 font-medium transition-colors ${lang==='en' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>EN</button>
              <button onClick={() => setLang('es')} aria-label="Cambiar a español"
                className={`px-3 py-1.5 font-medium transition-colors ${lang==='es' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>ES</button>
            </div>
          </div>
          <a href="/login" className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" aria-label={T.staffLogin}>
            {T.staffLogin} <ChevronRight size={12} aria-hidden="true" />
          </a>
        </div>
      </header>

      {/* Hero search */}
      <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-semibold text-white mb-2">{T.heroTitle}</h1>
          <p className="text-blue-200 text-sm mb-8">
            {facets.total_public > 0 ? `${facets.total_public.toLocaleString()} public records available` : 'Transparent access to official records'}
          </p>
          <div className="flex gap-2 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={handleKey}
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-white/30 bg-white"
                placeholder={T.placeholder} />
            </div>
            <button onClick={() => doSearch(1)}
              className="px-6 py-3 bg-white text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-50 transition-colors">
              Search
            </button>
          </div>

          {/* Type quick filters */}
          <div className="flex flex-wrap gap-2 justify-center mt-5">
            {['Resolution','Ordinance','Meeting Minutes','Map','Report'].map(type => (
              <button key={type} onClick={() => { setFilters(f => ({ ...f, type: f.type === type ? '' : type })); setTimeout(() => doSearch(1), 50); }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all font-medium
                  ${filters.type === type ? 'bg-white text-blue-700 border-white' : 'bg-white/10 text-white border-white/30 hover:bg-white/20'}`}>
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Dept filter */}
        {facets.departments?.length > 0 && (
          <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
            <Filter size={13} className="text-gray-400 flex-shrink-0" />
            <button onClick={() => { setFilters(f => ({ ...f, department: '' })); setTimeout(() => doSearch(1), 50); }}
              className={`text-xs px-3 py-1.5 rounded-full border flex-shrink-0 transition-all
                ${!filters.department ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              All departments
            </button>
            {facets.departments.slice(0, 8).map(d => (
              <button key={d.department} onClick={() => { setFilters(f => ({ ...f, department: f.department === d.department ? '' : d.department })); setTimeout(() => doSearch(1), 50); }}
                className={`text-xs px-3 py-1.5 rounded-full border flex-shrink-0 transition-all whitespace-nowrap
                  ${filters.department === d.department ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                {d.department}
              </button>
            ))}
          </div>
        )}

        {results && (
          <div className="text-sm text-gray-500 mb-4">
            {results.total === 0 ? 'No public records found' : `${results.total.toLocaleString()} record${results.total !== 1 ? 's' : ''}${q && searched ? ` matching "${q}"` : ''}`}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse h-36" />)}
          </div>
        )}

        {!loading && results?.results?.length === 0 && (
          <div className="text-center py-16">
            <FileText size={32} className="mx-auto text-gray-200 mb-3" />
            <div className="text-sm text-gray-400">No public records match your search</div>
            <button onClick={() => { setQ(''); setFilters({ type: '', department: '' }); doSearch(1, true); }}
              className="text-xs text-blue-600 mt-2 hover:text-blue-700">Clear search</button>
          </div>
        )}

        {!loading && results?.results?.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {results.results.map(r => (
                <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 hover:shadow-sm transition-all group">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText size={15} className="text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <TypeBadge type={r.document_type} />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 leading-snug">{r.title}</h3>
                      {r.snippet && (
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: r.snippet.replace(/<b>/g, '<strong>').replace(/<\/b>/g, '</strong>') }} />
                      )}
                      <div className="flex items-center gap-3 mt-2.5">
                        <span className="text-xs text-gray-400">{r.department}</span>
                        {r.published_at && <span className="text-xs text-gray-300">{format(new Date(r.published_at), 'MMM d, yyyy')}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-50">
                    {r.tags?.slice(0,3).map(tag => (
                      <span key={tag} className="text-xs bg-gray-50 text-gray-400 px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                    <a href={`${apiBase}/uploads/${r.file_path}`} target="_blank" rel="noopener noreferrer"
                      className="ml-auto flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium group-hover:gap-2 transition-all">
                      <Download size={12} /> Download
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {results.total > 12 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button disabled={page === 1} onClick={() => doSearch(page - 1)}
                  className="px-5 py-2 text-sm rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50">← Previous</button>
                <span className="text-sm text-gray-500">Page {page} of {Math.ceil(results.total / 12)}</span>
                <button disabled={page * 12 >= results.total} onClick={() => doSearch(page + 1)}
                  className="px-5 py-2 text-sm rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50">Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Citizen Q&A Section */}
      <div className="border-t border-gray-100 bg-gradient-to-b from-white to-gray-50 py-12 px-4 mt-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 text-xs font-medium px-3 py-1.5 rounded-full mb-3">
              <Sparkles size={12} /> AI-powered Q&A
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{T.askTitle}</h2>
            <p className="text-sm text-gray-500 mt-1">Ask in plain English — our AI searches published records to find the answer</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex gap-2 mb-4">
              <input
                value={ragQuestion}
                onChange={e => setRagQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && askCitizen()}
                placeholder={`e.g. ${T.askPlaceholder}`}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-50" />
              <button onClick={askCitizen} disabled={ragLoading || !ragQuestion.trim()}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-40 flex items-center gap-2 transition-all">
                {ragLoading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/> : <Send size={14} />}
                Ask
              </button>
            </div>

            {ragAnswer && (
              <div className="border-t border-gray-50 pt-4">
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 leading-relaxed mb-3">
                  {/* Same table support as the staff Ask page — a public answer
                      quoting a table must not render as a wall of pipe
                      characters, and a wide table must scroll rather than clip. */}
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({children}) => <li>{children}</li>,
                    code: ({children}) => <code className="px-1 py-0.5 rounded bg-white text-[0.85em] font-mono">{children}</code>,
                    table: ({children}) => (
                      <div className="my-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
                        <table className="min-w-full text-xs border-collapse">{children}</table>
                      </div>
                    ),
                    thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
                    tbody: ({children}) => <tbody>{children}</tbody>,
                    tr: ({children}) => <tr className="border-b border-gray-100 last:border-0">{children}</tr>,
                    th: ({children}) => <th className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">{children}</th>,
                    td: ({children}) => <td className="px-3 py-2 text-gray-700 whitespace-nowrap align-top">{children}</td>,
                    a: ({href, children}) => (
                      <a href={href} target="_blank" rel="noopener noreferrer nofollow"
                        className="text-purple-600 underline decoration-purple-200 hover:decoration-purple-500">{children}</a>
                    ),
                  }}>{ragAnswer.answer}</ReactMarkdown>
                </div>
                {ragAnswer.sources?.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-400 mb-2">Source records</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {ragAnswer.sources.map((s, i) => (
                        <a key={i}
                          href={`${apiBase}/uploads/${s.file_path}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg border border-gray-100 hover:border-purple-200 hover:bg-purple-50/20 transition-all text-xs">
                          <FileText size={12} className="text-purple-400 flex-shrink-0" />
                          <span className="font-medium text-gray-700 truncate">{s.title}</span>
                          <span className="text-gray-400 ml-auto flex-shrink-0">{s.document_type}</span>
                          <span className="text-teal-600 font-medium flex-shrink-0">{s.similarity}% match</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-300 mt-3 text-center">AI-generated answer — verify against source documents for official use</p>
              </div>
            )}

            {!ragAnswer && !ragLoading && (
              <div className="flex flex-wrap gap-2">
                {["What parks improvements were approved?","What are the latest ordinances?","Who can I contact about zoning?"].map(ex => (
                  <button key={ex} onClick={() => { setRagQuestion(ex); }}
                    className="text-xs text-gray-500 bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-full hover:border-purple-200 hover:text-purple-700 hover:bg-purple-50 transition-all">
                    {ex}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="border-t border-gray-100 bg-white py-6 px-4 text-center text-xs text-gray-400">
        {T.footer} · <a href="/login" className="text-blue-500 hover:text-blue-600">Staff access</a>
      </footer>
    </div>
  );
}
